import type { InputBarRoutingIntent } from "@/stores/input-bar-target-store";
import { useInputBarTarget } from "@/stores/input-bar-target-store";
import { subscribeDesktopChatAuthScope } from "@/desktop/desktop-chat-auth-scope";
import {
  clearPromptFailure,
  clearPromptNotices,
  clearPromptQueued,
  showPromptFailure,
  showPromptQueued,
} from "./prompt-receipt-notices";
export { PROMPT_QUEUED_TOAST_DELAY_MS } from "./prompt-receipt-notices";
import {
  adoptAcceptedContinuationRuntime,
  adoptAcceptedNewConversationRoute,
  adoptConversationSplit,
  type ConversationSplit,
} from "@/lib/accepted-new-conversation-route";
import {
  PROMPT_RETRY_TTL_MS,
  clearRetryIdentitiesForComposer,
  clearRetryIdentity,
  isRetryRequestRetained,
  readRetryIdentity,
  clearAllRetryIdentities,
  clearRetryIdentitiesForAuthChange,
  storeRetryIdentity,
  type PromptRetryIdentity,
  type PromptRetryStatus,
} from "@/lib/prompt-retry-identity-store";
import { translate } from "@/i18n";
import { libChatMessages } from "@/i18n/messages/lib-chat";
export type {
  PromptRetryIdentity,
  PromptRetryStatus,
} from "@/lib/prompt-retry-identity-store";

const PROMPT_RECEIPT_TIMEOUT_MS = 30_000;
// A server-accepted request can wait for compaction recovery and fresh turn
// startup; this is not the ordinary unacknowledged-request deadline.
const PENDING_DELIVERY_TIMEOUT_MS = 90_000;
const MAX_RECEIPT_RECORDS = 100;

export interface PromptReceiptPayload {
  client_request_id: string | null;
  accepted: boolean;
  execution_id?: string;
  conversation_id?: string;
  runtime_session_id?: string;
  adapter_type?: string | null;
  model?: string | null;
  thinking_effort?: string | null;
  runtime_source?: string | null;
  message?: string;
  retryable?: boolean;
  delivery_pending?: boolean;
  execution_status?: string;
  prompt_source?: string;
  delivery_identity?: string;
  suppress_acknowledgement?: boolean;
  conversation_split?: ConversationSplit;
  steered_into_execution_id?: string;
  agent_name?: string;
  origin_display_id?: number;
}

interface PromptReceiptRecord {
  retryKey: string;
  composerId: string;
  routingIntent: InputBarRoutingIntent | null;
  onDefinitiveRejection?: () => void;
  onTimeout?: () => void;
  onAccepted?: () => void;
  timedOut: boolean;
  accepted?: boolean;
  createdAt: number;
  expiresAt: number;
}

interface PendingPromptReceipt extends PromptReceiptRecord {
  promise: Promise<boolean>;
  resolve: (accepted: boolean) => void;
  timer: ReturnType<typeof setTimeout>;
  deliveryPending: boolean;
}

const pendingReceipts = new Map<string, PendingPromptReceipt>();
const receiptRecords = new Map<string, PromptReceiptRecord>();
const invalidatedRequestIds = new Set<string>();
const retiredAuthRequests = new Map<string, number>();

subscribeDesktopChatAuthScope(() => {
  const routing = useInputBarTarget.getState();
  const requestIds = [
    ...receiptRecords.keys(),
    ...Object.keys(routing.promptRoutingRequests),
    routing.intentId,
    routing.claimedIntentId,
  ];
  for (const requestId of requestIds) {
    if (requestId)
      retiredAuthRequests.set(requestId, Date.now() + PROMPT_RETRY_TTL_MS);
  }
  clearReceiptState(true);
});

function pruneReceiptRecords(now = Date.now()): void {
  const stale = [...receiptRecords.entries()]
    .filter(([requestId, record]) =>
      pendingReceipts.has(requestId) ? false : record.expiresAt <= now,
    )
    .map(([requestId]) => requestId);
  const excess = [...receiptRecords.entries()]
    .filter(([requestId]) => !pendingReceipts.has(requestId))
    .sort((first, second) => second[1].createdAt - first[1].createdAt)
    .slice(MAX_RECEIPT_RECORDS)
    .map(([requestId]) => requestId);
  for (const requestId of new Set([...stale, ...excess])) {
    const record = receiptRecords.get(requestId);
    if (!record) continue;
    clearPromptQueued(requestId);
    clearRetryIdentity(record.retryKey, requestId);
    if (!record.accepted) record.onDefinitiveRejection?.();
    receiptRecords.delete(requestId);
  }
}

export function isPromptReceiptPending(clientRequestId: string): boolean {
  return pendingReceipts.has(clientRequestId);
}

export function isPromptRequestRetained(clientRequestId: string): boolean {
  return isRetryRequestRetained(clientRequestId);
}

export function promptReceiptRecordCountForTests(): number {
  return receiptRecords.size;
}

function updateRetainedRetryStatus(
  record: Pick<
    PromptReceiptRecord,
    "retryKey" | "composerId" | "routingIntent"
  >,
  requestId: string,
  status: PromptRetryStatus,
): boolean {
  const identity = readRetryIdentity(record.retryKey);
  if (
    identity?.requestId !== requestId ||
    identity.composerId !== record.composerId
  ) {
    return false;
  }
  storeRetryIdentity(record.retryKey, { ...identity, status });
  return true;
}

export function clearPromptRetryIdentitiesForComposer(
  composerId: string,
): void {
  clearRetryIdentitiesForComposer(composerId);
  for (const [requestId, record] of receiptRecords) {
    if (record.composerId !== composerId) continue;
    invalidatedRequestIds.add(requestId);
    clearPromptQueued(requestId);
    if (!pendingReceipts.has(requestId)) {
      if (!record.accepted) record.onDefinitiveRejection?.();
      receiptRecords.delete(requestId);
    }
  }
}

function sameTarget(
  first: InputBarRoutingIntent["target"],
  second: InputBarRoutingIntent["target"],
): boolean {
  return first?.conversationId === second?.conversationId;
}

export function promptRetryIdentityForKey(
  retryKey: string,
  currentIntent: InputBarRoutingIntent | null,
): PromptRetryIdentity | null {
  const identity = readRetryIdentity(retryKey);
  if (!identity) return null;
  const storedIntent = identity.routingIntent;
  if (identity.status !== "late_accepted") {
    return storedIntent?.intentId === currentIntent?.intentId ? identity : null;
  }
  if (storedIntent?.startNewConversation) {
    return !currentIntent?.intentId ? identity : null;
  }
  if (storedIntent?.target) {
    return sameTarget(storedIntent.target, currentIntent?.target ?? null)
      ? identity
      : null;
  }
  return !currentIntent?.intentId ? identity : null;
}

export function clientRequestIdForPrompt({
  retryKey,
  composerId = "unowned",
  routingIntent,
  requestedClientRequestId,
}: {
  retryKey: string;
  composerId?: string;
  routingIntent: InputBarRoutingIntent | null;
  requestedClientRequestId?: string;
}): string {
  const existing = promptRetryIdentityForKey(retryKey, routingIntent);
  const clientRequestId =
    requestedClientRequestId ?? existing?.requestId ?? crypto.randomUUID();
  const now = Date.now();
  storeRetryIdentity(retryKey, {
    version: 1,
    requestId: clientRequestId,
    composerId,
    routingIntent,
    status:
      existing?.requestId === clientRequestId &&
      existing.status === "late_accepted"
        ? "late_accepted"
        : "pending",
    createdAt: existing?.createdAt ?? now,
    expiresAt: existing?.expiresAt ?? now + PROMPT_RETRY_TTL_MS,
  });
  return clientRequestId;
}

function timeoutPromptReceipt(clientRequestId: string): void {
  const pending = pendingReceipts.get(clientRequestId);
  if (!pending) return;
  pendingReceipts.delete(clientRequestId);
  if (invalidatedRequestIds.delete(clientRequestId)) {
    receiptRecords.delete(clientRequestId);
    pending.onDefinitiveRejection?.();
  } else {
    receiptRecords.set(clientRequestId, { ...pending, timedOut: true });
    pruneReceiptRecords();
  }
  updateRetainedRetryStatus(pending, clientRequestId, "timed_out");
  pending.onTimeout?.();
  pending.resolve(false);
  clearPromptQueued(clientRequestId);
  if (pending.deliveryPending) {
    showPromptFailure(
      clientRequestId,
      translate(libChatMessages, "deliveryUnconfirmed"),
      translate(libChatMessages, "requestStatusUnknown"),
    );
  } else {
    showPromptFailure(
      clientRequestId,
      translate(libChatMessages, "serverDidNotConfirm"),
    );
  }
}

export function waitForPromptReceipt({
  clientRequestId,
  retryKey,
  composerId = "unowned",
  routingIntent,
  onDefinitiveRejection,
  onTimeout,
  onAccepted,
}: {
  clientRequestId: string;
  retryKey: string;
  composerId?: string;
  routingIntent: InputBarRoutingIntent | null;
  onDefinitiveRejection?: () => void;
  onTimeout?: () => void;
  onAccepted?: () => void;
}): Promise<boolean> {
  pruneReceiptRecords();
  clearPromptFailure(clientRequestId);
  const existing = pendingReceipts.get(clientRequestId);
  if (existing) return existing.promise;
  const retained = readRetryIdentity(retryKey);
  if (
    receiptRecords.get(clientRequestId)?.accepted ||
    (retained?.requestId === clientRequestId &&
      retained.composerId === composerId &&
      retained.status === "late_accepted")
  ) {
    onAccepted?.();
    return Promise.resolve(true);
  }
  let resolvePending!: (accepted: boolean) => void;
  const promise = new Promise<boolean>((resolve) => {
    resolvePending = resolve;
  });
  const record: PromptReceiptRecord = {
    retryKey,
    composerId,
    routingIntent,
    onDefinitiveRejection,
    onTimeout,
    onAccepted,
    timedOut: false,
    createdAt: Date.now(),
    expiresAt: Date.now() + PROMPT_RETRY_TTL_MS,
  };
  const timer = setTimeout(
    () => timeoutPromptReceipt(clientRequestId),
    PROMPT_RECEIPT_TIMEOUT_MS,
  );
  const pending = {
    ...record,
    promise,
    resolve: resolvePending,
    timer,
    deliveryPending: false,
  };
  pendingReceipts.set(clientRequestId, pending);
  receiptRecords.set(clientRequestId, record);
  return promise;
}

export function applyPromptReceipt(receipt: PromptReceiptPayload): boolean {
  pruneReceiptRecords();
  const requestId = receipt.client_request_id;
  if (!requestId) return false;
  for (const [id, expiresAt] of retiredAuthRequests) {
    if (expiresAt <= Date.now()) retiredAuthRequests.delete(id);
  }
  if (retiredAuthRequests.has(requestId)) return false;
  if (receipt.accepted && receipt.delivery_pending !== true)
    clearPromptFailure(requestId);
  const pending = pendingReceipts.get(requestId);
  const record = pending ?? receiptRecords.get(requestId);
  if (record?.accepted && !pending) return true;
  const routingState = useInputBarTarget.getState();
  const routingRequest = routingState.promptRoutingRequests[requestId];
  const currentNewConversationIntent =
    routingState.startNewConversation && routingState.intentId === requestId
      ? routingState.snapshotRoutingIntent()
      : null;
  const routingKnown = Boolean(routingRequest || currentNewConversationIntent);
  const routingIntent =
    record?.routingIntent ??
    routingRequest?.intent ??
    currentNewConversationIntent;
  if (
    !receipt.accepted &&
    receipt.message?.trim() === "prompt request is still being accepted"
  ) {
    return !!record || routingKnown;
  }
  const adoptedNewConversation =
    receipt.accepted && routingIntent
      ? adoptAcceptedNewConversationRoute(receipt, routingIntent)
      : false;
  if (receipt.accepted && (record || routingKnown)) {
    adoptConversationSplit(receipt);
    adoptAcceptedContinuationRuntime(receipt, routingIntent);
  }
  if (receipt.accepted && receipt.delivery_pending === true) {
    if (pending && !pending.deliveryPending) {
      clearTimeout(pending.timer);
      pending.deliveryPending = true;
      pending.timer = setTimeout(
        () => timeoutPromptReceipt(requestId),
        PENDING_DELIVERY_TIMEOUT_MS,
      );
    }
    const canShowQueued =
      (record || routingKnown) &&
      !record?.timedOut &&
      !invalidatedRequestIds.has(requestId);
    if (canShowQueued) showPromptQueued(requestId, receipt.message);
    else clearPromptQueued(requestId);
    return !!record || routingKnown;
  }
  clearPromptQueued(requestId);
  const routingSettled = receipt.retryable
    ? false
    : useInputBarTarget
        .getState()
        .settlePromptRoutingRequest(requestId, receipt.accepted);
  if (!record) {
    return (
      adoptedNewConversation ||
      routingSettled ||
      (receipt.retryable === true && routingKnown)
    );
  }
  if (!receipt.accepted && receipt.retryable) {
    if (pending) {
      clearTimeout(pending.timer);
      pendingReceipts.delete(requestId);
      pending.resolve(false);
    }
    if (invalidatedRequestIds.delete(requestId)) {
      record.onDefinitiveRejection?.();
      receiptRecords.delete(requestId);
      return true;
    }
    updateRetainedRetryStatus(record, requestId, "timed_out");
    receiptRecords.set(requestId, { ...record, timedOut: true });
    pruneReceiptRecords();
    showPromptFailure(
      requestId,
      receipt.message?.trim() ||
        translate(libChatMessages, "savedNotDelivered"),
    );
    return true;
  }
  if (pending) {
    clearTimeout(pending.timer);
    pendingReceipts.delete(requestId);
    pending.resolve(receipt.accepted);
  }
  invalidatedRequestIds.delete(requestId);
  if (receipt.accepted && record.routingIntent) {
    useInputBarTarget.getState().consumeRoutingIntent(record.routingIntent);
  }
  if (receipt.accepted) {
    record.accepted = true;
    record.onAccepted?.();
  }
  if (receipt.accepted && record.timedOut && !pending) {
    updateRetainedRetryStatus(record, requestId, "late_accepted");
  } else {
    clearRetryIdentity(record.retryKey, requestId);
    receiptRecords.delete(requestId);
  }
  if (!receipt.accepted) {
    record.onDefinitiveRejection?.();
    receiptRecords.delete(requestId);
    showPromptFailure(
      requestId,
      receipt.message?.trim() || translate(libChatMessages, "serverRejected"),
    );
  }
  return true;
}

export function rejectPendingPromptReceipt(
  clientRequestId: string,
  message: string,
): boolean {
  return applyPromptReceipt({
    client_request_id: clientRequestId,
    accepted: false,
    message,
  });
}

export function markPendingPromptReceiptRetryableFailure(
  clientRequestId: string,
  message: string,
): boolean {
  return applyPromptReceipt({
    client_request_id: clientRequestId,
    accepted: false,
    retryable: true,
    message,
  });
}

function clearReceiptState(authChanged = false): void {
  for (const pending of pendingReceipts.values()) {
    clearTimeout(pending.timer);
    pending.resolve(false);
  }
  pendingReceipts.clear();
  receiptRecords.clear();
  clearPromptNotices();
  if (authChanged) clearRetryIdentitiesForAuthChange();
  else clearAllRetryIdentities();
  invalidatedRequestIds.clear();
}

export function resetPromptReceiptBusForTests(): void {
  clearReceiptState();
  retiredAuthRequests.clear();
}
